import { logError } from './lib/logger.js'
import React, { useState, useEffect, useRef, createContext, useContext, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import Fuse from 'fuse.js'
import { supabase } from './lib/supabase.js'
import AuthPage from './components/AuthPage.jsx'
import { ErrorBoundary } from './components/ErrorRetry.jsx'

const Dashboard = lazy(() => import('./components/Dashboard.jsx'))
const TuteurDashboard = lazy(() => import('./components/tuteur/TuteurDashboard.jsx'))
const PatronDashboard = lazy(() => import('./components/patron/PatronDashboard.jsx'))
const InscriptionEcurie = lazy(() => import('./components/patron/InscriptionEcurie.jsx'))
const ParentView = lazy(() => import('./components/ParentView.jsx'))
const LandingPage = lazy(() => import('./components/LandingPage.jsx'))
const DemoPage = lazy(() => import('./components/DemoPage.jsx'))
const PricingPage = lazy(() => import('./components/PricingPage.jsx'))

// ── Global CSS ────────────────────────────────────────────────────────────────
const globalStyle = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:      #FAF7F2;
    --bg2:     #EFE7DC;
    --bg3:     #E5D9CC;
    --card:    #EFE7DCcc;
    --bd:      #6B4F3A22;
    --t1:      #1F1F1F;
    --t2:      #4a3728;
    --t3:      #9a8070;
    --gold:    #6B4F3A;
    --gold2:   #8B6F5A;
    --green:   #9CAF88;
    --red:     #c0392b;
    --blue:    #5b8fa8;
    --purple:  #9b7fb5;
    --font-display: 'Fraunces', Georgia, serif;
    --font-body: 'DM Sans', system-ui, sans-serif;
    --radius: 14px;
    --shadow: 0 8px 32px rgba(107,79,58,.12);
  }
  [data-theme="light"] {
    --bg:      #FDF8F3;
    --bg2:     #F5EAE0;
    --bg3:     #EDD8C8;
    --card:    #F5EAE0cc;
    --bd:      #5C1A2420;
    --t1:      #2A0C10;
    --t2:      #6A4030;
    --t3:      #A08880;
    --gold:    #D4A860;
    --gold2:   #E8C080;
    --bordeaux:#5C1A24;
    --bord2:   #742230;
    --green:   #1D9E75;
    --red:     #c0392b;
    --blue:    #5b8fa8;
    --purple:  #9b7fb5;
    --shadow: 0 8px 32px rgba(92,26,36,.10);
  }
  .onboarding-select {
    color-scheme: light;
    background: #F7F3EE !important;
    color: #1A1410 !important;
  }
  .onboarding-select option {
    background: #F7F3EE;
    color: #1A1410;
  }

  /* ── CURSOR & HOVER EFFECTS ── */
  .sf-card { transition: transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s, border-color .25s !important }
  .sf-card:hover { transform: translateY(-6px) scale(1.02) !important; box-shadow: 0 16px 48px rgba(0,0,0,.25) !important; border-color: rgba(201,169,110,.4) !important }
  .sf-card-sm { transition: transform .2s cubic-bezier(.22,1,.36,1), box-shadow .2s !important }
  .sf-card-sm:hover { transform: translateY(-3px) scale(1.015) !important; box-shadow: 0 8px 24px rgba(0,0,0,.2) !important }
  .sf-btn { transition: all .2s cubic-bezier(.34,1.56,.64,1) !important }
  .sf-btn:hover { transform: translateY(-3px) scale(1.04) !important; box-shadow: 0 8px 20px rgba(201,169,110,.35) !important }
  .sf-btn:active { transform: scale(.95) !important }
  .sf-row { transition: transform .2s cubic-bezier(.22,1,.36,1), background .2s, padding-left .2s !important }
  .sf-row:hover { transform: translateX(8px) !important; background: var(--bg3) !important }
  .sf-icon { transition: transform .3s cubic-bezier(.34,1.56,.64,1) !important; display:inline-block }
  .sf-icon:hover { transform: scale(1.2) rotate(8deg) !important }
  .sf-stat { transition: transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s !important; background: transparent !important; border: none !important; box-shadow: none !important; }
  .sf-stat:hover { transform: translateY(-8px) scale(1.06) !important; box-shadow: 0 16px 40px rgba(0,0,0,.2) !important }
  .sf-pill { transition: all .2s cubic-bezier(.34,1.56,.64,1) !important }
  .sf-pill:hover { transform: scale(1.08) translateY(-2px) !important }
  .sf-link { transition: color .15s, transform .15s !important; display:inline-block }
  .sf-link:hover { color: var(--gold) !important; transform: translateX(4px) !important }
  .sf-avatar { transition: transform .3s cubic-bezier(.34,1.56,.64,1) !important }
  .sf-avatar:hover { transform: scale(1.15) rotate(5deg) !important }
  .sf-input { transition: border-color .2s, box-shadow .2s !important }
  .sf-input:focus { border-color: var(--gold) !important; box-shadow: 0 0 0 3px rgba(201,169,110,.15) !important }
  .sf-tab { transition: all .2s cubic-bezier(.34,1.56,.64,1) !important }
  .sf-tab:hover { transform: translateY(-2px) scale(1.05) !important }
  .sf-tab.active { transform: translateY(-1px) !important }
  /* Magnetic effect via CSS */
  .sf-magnetic { transition: transform .3s cubic-bezier(.22,1,.36,1) !important }
  /* Glow on hover for gold elements */
  .sf-gold-hover { transition: all .25s !important }
  .sf-gold-hover:hover { color: var(--gold) !important; text-shadow: 0 0 12px rgba(201,169,110,.5) !important }

  /* ── MEGA ANIMATIONS ── */
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes glow { 0%,100%{box-shadow:0 0 0 0 rgba(201,169,110,0)} 50%{box-shadow:0 0 20px 4px rgba(201,169,110,.25)} }
  @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
  @keyframes ripple { 0%{transform:scale(0);opacity:.6} 100%{transform:scale(4);opacity:0} }
  @keyframes slideInUp { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slideInLeft { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes popIn { 0%{transform:scale(0);opacity:0} 70%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
  @keyframes borderGlow { 0%,100%{border-color:rgba(201,169,110,.2)} 50%{border-color:rgba(201,169,110,.6)} }
  @keyframes bgPulse { 0%,100%{background-size:100% 100%} 50%{background-size:110% 110%} }
  @keyframes countUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes blobFloat { 0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;transform:translateY(0)} 50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;transform:translateY(-12px)} }

  .anim-float { animation: float 4s ease-in-out infinite }
  .anim-glow { animation: glow 2.5s ease-in-out infinite }
  .anim-shimmer { background: linear-gradient(90deg,transparent,rgba(201,169,110,.15),transparent); background-size:200% 100%; animation: shimmer 2s linear infinite }
  .anim-slide-up { animation: slideInUp .5s cubic-bezier(.22,1,.36,1) both }
  .anim-pop { animation: popIn .4s cubic-bezier(.34,1.56,.64,1) both }
  .anim-border-glow { animation: borderGlow 2s ease-in-out infinite }

  /* Hover effects globaux */
  .hover-lift { transition: transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s !important }
  .hover-lift:hover { transform: translateY(-4px) !important; box-shadow: 0 12px 32px rgba(0,0,0,.2) !important }
  .hover-scale { transition: transform .2s cubic-bezier(.34,1.56,.64,1) !important }
  .hover-scale:hover { transform: scale(1.04) !important }
  .hover-glow:hover { box-shadow: 0 0 20px rgba(201,169,110,.3) !important }

  /* Card shine effect */
  .card-shine { position:relative; overflow:hidden }
  .card-shine::after { content:''; position:absolute; top:-50%; left:-75%; width:50%; height:200%; background:linear-gradient(90deg,transparent,rgba(255,255,255,.06),transparent); transform:skewX(-20deg); transition:left .6s }
  .card-shine:hover::after { left:125% }

  /* Gold button */
  .btn-gold-anim { position:relative; overflow:hidden; transition:all .25s !important }
  .btn-gold-anim::before { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent,rgba(255,255,255,.2),transparent); transform:translateX(-100%); transition:transform .4s }
  .btn-gold-anim:hover::before { transform:translateX(100%) }
  .btn-gold-anim:hover { transform:translateY(-2px) !important; box-shadow:0 8px 24px rgba(201,169,110,.4) !important }
  .btn-gold-anim:active { transform:translateY(0) scale(.97) !important }

  /* Blob background */
  .blob-bg { position:absolute; border-radius:60% 40% 30% 70%/60% 30% 70% 40%; filter:blur(60px); pointer-events:none; animation:blobFloat 8s ease-in-out infinite; will-change:transform }

  /* Stagger children */
  .stagger > * { animation: slideInUp .4s cubic-bezier(.22,1,.36,1) both }
  .stagger > *:nth-child(1){animation-delay:.05s}
  .stagger > *:nth-child(2){animation-delay:.1s}
  .stagger > *:nth-child(3){animation-delay:.15s}
  .stagger > *:nth-child(4){animation-delay:.2s}
  .stagger > *:nth-child(5){animation-delay:.25s}
  .stagger > *:nth-child(6){animation-delay:.3s}

  /* Nav active indicator */
  .nav-active { position:relative }
  .nav-active::before { content:''; position:absolute; left:0; top:50%; transform:translateY(-50%); width:3px; height:60%; background:var(--gold); border-radius:0 3px 3px 0; animation:popIn .3s cubic-bezier(.34,1.56,.64,1) }

  /* Section transitions */
  .section-enter { animation: slideInUp .4s cubic-bezier(.22,1,.36,1) both }

  /* Skeleton loader */
  @keyframes skeleton { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  .skeleton { background:linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%); background-size:200% 100%; animation:skeleton 1.5s infinite }

  /* Page transition */
  @keyframes pageFadeIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .page-enter { animation: pageFadeIn .4s cubic-bezier(.22,1,.36,1) both }

  /* Ripple button */
  .ripple-btn { position:relative; overflow:hidden }
  .ripple-btn:active::after { content:''; position:absolute; border-radius:50%; background:rgba(255,255,255,.3); width:100px; height:100px; margin-top:-50px; margin-left:-50px; top:50%; left:50%; animation:ripple .6s linear }

  [data-theme="light"] body {
    background: var(--bg);
    color: var(--t1);
  }
  [data-theme="light"] ::-webkit-scrollbar-thumb { background: #6B4F3A33; }
  [data-theme="light"] ::selection { background: #6B4F3A22; }
  html { scroll-behavior: smooth; }
  body {
    background: var(--bg);
    color: var(--t1);
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
  }
  /* Scrollbar */
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--bd); border-radius: 3px; }
  /* Selection */
  ::selection { background: var(--gold)33; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: .5; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .fade-up  { animation: fadeUp  .5s ease both; }
  .fade-in  { animation: fadeIn  .4s ease both; }
  .spinner  { animation: spin .8s linear infinite; }

  /* ── PALETTE BORDEAUX OR — overrides globaux thème light ── */
  [data-theme="light"] button,
  [data-theme="light"] .sf-btn {
    font-family: var(--font-body);
  }

  /* Topbar stats chips */
  [data-theme="light"] .sf-stat {
    background: var(--bg2) !important;
    border-color: var(--bd) !important;
  }

  /* Sidebar nav item actif */
  [data-theme="light"] .nav-active::before {
    background: var(--bordeaux, #5C1A24) !important;
  }

  /* Badges difficulté : remapper les couleurs criardes */
  [data-theme="light"] [style*="color: rgb(248, 113, 113)"],
  [data-theme="light"] [style*="color:#f87171"],
  [data-theme="light"] [style*='color: "#f87171"'] {
    color: #A32D2D !important;
  }
  [data-theme="light"] [style*="background: rgb(248, 113, 113)"],
  [data-theme="light"] [style*="background:#f87171"] {
    background: #F5C4C4 !important;
  }

  /* Purple → bordeaux en light mode */
  [data-theme="light"] [style*="color:#a78bfa"],
  [data-theme="light"] [style*="color: #a78bfa"],
  [data-theme="light"] [style*="color: rgb(167, 139, 250)"] {
    color: var(--bordeaux, #5C1A24) !important;
  }
  [data-theme="light"] [style*="background:#a78bfa"],
  [data-theme="light"] [style*="background: #a78bfa"] {
    background: var(--bordeaux, #5C1A24) !important;
  }
  [data-theme="light"] [style*="border-color:#a78bfa"],
  [data-theme="light"] [style*="borderColor:#a78bfa"] {
    border-color: var(--bordeaux, #5C1A24) !important;
  }

  /* Blue accent → or en light mode */
  [data-theme="light"] [style*="color:#60a5fa"],
  [data-theme="light"] [style*="color: #60a5fa"],
  [data-theme="light"] [style*="color: rgb(96, 165, 250)"] {
    color: var(--gold, #D4A860) !important;
  }

  /* Green accent → garder mais adoucir */
  [data-theme="light"] [style*="color:#34d399"],
  [data-theme="light"] [style*="color: #34d399"],
  [data-theme="light"] [style*="color: rgb(52, 211, 153)"] {
    color: #1D7A5A !important;
  }

  /* Cards en light mode */
  [data-theme="light"] .sf-card {
    background: var(--bg2) !important;
    border-color: var(--bd) !important;
  }

  /* Tabs/pills actifs → bordeaux */
  [data-theme="light"] [style*="background: var(--gold)"],
  [data-theme="light"] [style*="background:var(--gold)"] {
    background: var(--bordeaux, #5C1A24) !important;
    color: var(--gold, #D4A860) !important;
  }

  /* Scrollbar en crème */
  [data-theme="light"] ::-webkit-scrollbar-thumb {
    background: var(--bordeaux, #5C1A24) !important;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    body { overflow-x: hidden; }
    
    /* Sidebar cachée sur mobile */
    .sf-sidebar { display: none !important; }
    
    /* Nav mobile en bas */
    .sf-mobile-nav {
      display: flex !important;
      position: fixed;
      bottom: 0; left: 0; right: 0;
      background: var(--bg2);
      border-top: 1px solid var(--bd);
      z-index: 1000;
      padding: 8px 0 12px;
      justify-content: space-around;
      align-items: center;
    }
    
    /* Contenu principal prend toute la largeur */
    .sf-main { padding-bottom: 80px !important; }
    
    /* Cards en colonne sur mobile */
    .sf-grid { grid-template-columns: 1fr !important; }
    
    /* Header stats compact */
    .sf-header-stats { display: none !important; }
    
    /* Modals plein écran sur mobile */
    .sf-modal {
      position: fixed !important;
      inset: 0 !important;
      border-radius: 0 !important;
      max-width: 100% !important;
      max-height: 100% !important;
      overflow-y: auto;
    }
  }
`

// ── Auth Context ──────────────────────────────────────────────────────────────
export const AuthCtx = createContext(null)
export const useAuth = () => useContext(AuthCtx)

// Démo publique du tableau de bord gestionnaire — accessible sans compte
function PatronDemoPublic() {
  const navigate = useNavigate()
  useEffect(() => { try { sessionStorage.setItem('patron-demo', 'true') } catch { /* noop */ } }, [])
  const demoAuth = {
    session: null,
    profile: { id: null, full_name: 'Prépa Démo', email: 'demo@flashia.io', role_ecurie: 'patron' },
    signOut: () => navigate('/'),
    reloadProfile: () => {},
    setPage: navigate,
  }
  return (
    <AuthCtx.Provider value={demoAuth}>
      <PatronDashboard />
    </AuthCtx.Provider>
  )
}

// Démo publique du tableau de bord étudiant — accessible sans compte
function StudentDemoPublic() {
  const navigate = useNavigate()
  useEffect(() => { try { sessionStorage.setItem('student-demo', 'true') } catch { /* noop */ } }, [])
  const demoAuth = {
    session: null,
    profile: { id: null, full_name: 'Élève Démo', email: 'demo-eleve@flashia.io', role: 'student', plan: 'premium' },
    signOut: () => navigate('/'),
    reloadProfile: () => {},
    setPage: navigate,
    pendingSection: null,
    setPendingSection: () => {},
  }
  return (
    <AuthCtx.Provider value={demoAuth}>
      <Dashboard />
    </AuthCtx.Provider>
  )
}

// ── App ───────────────────────────────────────────────────────────────────────

const UNIVERSITES = [
  "Aix-Marseille Université",
  "Autre",
  "CY Cergy Paris Université",
  "ENS Lyon",
  "ENS Paris-Saclay",
  "Institut National des Sciences Appliquées (INSA)",
  "Sciences Po Bordeaux",
  "Sciences Po Grenoble",
  "Sciences Po Lille",
  "Sciences Po Lyon",
  "Sciences Po Paris",
  "Sciences Po Rennes",
  "Sciences Po Strasbourg",
  "Sciences Po Toulouse",
  "Sorbonne Université",
  "Université Clermont Auvergne",
  "Université d\'Orléans",
  "Université d\'Évry",
  "Université de Bordeaux",
  "Université de Bourgogne",
  "Université de Bretagne Occidentale",
  "Université de Bretagne Sud",
  "Université de Caen Normandie",
  "Université de Corse Pasquale Paoli",
  "Université de Franche-Comté",
  "Université de Grenoble Alpes",
  "Université de Guyane",
  "Université de Haute-Alsace",
  "Université de La Rochelle",
  "Université de La Réunion",
  "Université de Lille",
  "Université de Limoges",
  "Université de Lorraine",
  "Université de Montpellier",
  "Université de Nantes",
  "Université de Nouvelle-Calédonie",
  "Université de Nîmes",
  "Université de Pau et des Pays de l\'Adour",
  "Université de Perpignan",
  "Université de Picardie Jules Verne",
  "Université de Poitiers",
  "Université de Polynésie Française",
  "Université de Reims Champagne-Ardenne",
  "Université de Rennes 1",
  "Université de Rouen Normandie",
  "Université de Savoie Mont Blanc",
  "Université de Strasbourg",
  "Université de Technologie de Belfort-Montbéliard",
  "Université de Technologie de Compiègne",
  "Université de Technologie de Troyes",
  "Université de Toulon",
  "Université de Tours",
  "Université de Valenciennes",
  "Université de Versailles Saint-Quentin",
  "Université des Antilles",
  "Université du Maine",
  "Université Gustave Eiffel",
  "Université Jean Monnet Saint-Étienne",
  "Université Jean Moulin Lyon 3",
  "Université Le Havre Normandie",
  "Université Lumière Lyon 2",
  "Université Nice Côte d\'Azur",
  "Université Paris 1 Panthéon-Sorbonne",
  "Université Paris 13 Nord",
  "Université Paris 2 Panthéon-Assas",
  "Université Paris 4 Sorbonne",
  "Université Paris 8 Vincennes",
  "Université Paris Descartes",
  "Université Paris Diderot",
  "Université Paris Est Créteil",
  "Université Paris Nanterre",
  "Université Paris-Dauphine",
  "Université Paris-Saclay",
  "Université Rennes 2",
  "Université Sorbonne Nouvelle Paris 3",
  "Université Toulouse 1 Capitole",
  "Université Toulouse 2 Jean Jaurès",
  "Université Toulouse 3 Paul Sabatier",
  "École Normale Supérieure Paris"
]

const FILIERES = [
  "Administration Publique",
  "AES (Administration Économique et Sociale)",
  "Anthropologie",
  "Architecture",
  "Archéologie",
  "Arts Plastiques",
  "Astronomie",
  "Automatique",
  "Autre",
  "Biochimie",
  "Biologie",
  "BTS Audiovisuel",
  "BTS Bâtiment",
  "BTS Communication",
  "BTS Comptabilité Gestion",
  "BTS Conception Produits Industriels",
  "BTS Gestion PME",
  "BTS Hôtellerie-Restauration",
  "BTS Maintenance Industrielle",
  "BTS MCO (Management Commercial Opérationnel)",
  "BTS NDRC (Négociation et Digitalisation)",
  "BTS SIO (Informatique)",
  "BTS Tourisme",
  "BTS Travaux Publics",
  "BTS Édition",
  "BTS Électrotechnique",
  "BTS Études et Économie de la Construction",
  "BTSA",
  "BTSM",
  "BUT Biologie",
  "BUT Carrières Sociales",
  "BUT Chimie",
  "BUT GEA",
  "BUT Génie Chimique",
  "BUT Génie Civil",
  "BUT Génie Mécanique",
  "BUT Génie Électrique",
  "BUT Information-Communication",
  "BUT Informatique",
  "BUT Mesures Physiques",
  "BUT MMI (Multimédia)",
  "BUT Réseaux et Télécommunications",
  "BUT Techniques de Commercialisation",
  "Chimie",
  "Cinéma",
  "Comptabilité",
  "CUPGE",
  "Cybersécurité",
  "Data Science",
  "DCG (Comptabilité Gestion)",
  "Design",
  "DEUST",
  "Diplôme d\'architecte",
  "Diplôme d\'ingénieur",
  "Diplôme d\'école de commerce",
  "DN MADE (Métiers d\'Art et du Design)",
  "Doctorat",
  "Droit des Affaires",
  "Droit Fiscal",
  "Droit International",
  "Droit Privé",
  "Droit Public",
  "Droit Pénal",
  "DSCG",
  "Développement Logiciel",
  "Ergothérapeute",
  "Finance",
  "Gestion",
  "Génie Chimique",
  "Génie Civil",
  "Génie Industriel",
  "Génie Mécanique",
  "Génie Électrique",
  "Géographie",
  "Histoire",
  "Hypokhâgne",
  "Infirmier(e)",
  "Informatique",
  "Intelligence Artificielle",
  "Interprétation",
  "Khâgne",
  "Kinésithérapie",
  "LEA",
  "Lettres Classiques",
  "Lettres Modernes",
  "Licence Pro",
  "Littérature Comparée",
  "LLCER",
  "Management",
  "Manipulateur radio",
  "Marketing",
  "Mathématiques",
  "Maïeutique (Sage-femme)",
  "Médecine",
  "Médecine vétérinaire",
  "Métiers du Multimédia et Internet",
  "Neuropsychologie",
  "Odontologie",
  "Orthophoniste",
  "Orthoptiste",
  "Pharmacie",
  "Philosophie",
  "Physique",
  "Prépa ATS",
  "Prépa BCPST",
  "Prépa ECG",
  "Prépa ECT",
  "Prépa MP",
  "Prépa MP2I",
  "Prépa MPI",
  "Prépa MPSI",
  "Prépa PC",
  "Prépa PCSI",
  "Prépa PSI",
  "Prépa PT",
  "Prépa PTSI",
  "Prépa TB",
  "Prépa TSI",
  "Psychologie Clinique",
  "Psychologie Cognitive",
  "Psychologie Sociale",
  "Psychomotricien(ne)",
  "Relations Internationales",
  "Robotique",
  "Réseaux et Télécommunications",
  "Science Politique",
  "Sciences de la Terre",
  "Sociologie",
  "STAPS - Entraînement Sportif",
  "STAPS - Management du Sport",
  "STAPS - Éducation et Motricité",
  "Statistiques",
  "SVT",
  "Théâtre",
  "Traduction",
  "Économie",
  "Électronique"
]

const ANNEES = [
  // Licence
  "L1 (Bac+1)","L2 (Bac+2)","L3 (Bac+3)",
  // Master
  "M1 (Bac+4)","M2 (Bac+5)",
  // Doctorat
  "Doctorat 1ère année","Doctorat 2ème année","Doctorat 3ème année","Post-doc",
  // Médecine
  "PASS","LAS 1","LAS 2","LAS 3",
  "DFGSM2","DFGSM3","DFASM1","DFASM2","DFASM3",
  "Externat","Internat",
  // Pharmacie
  "Pharmacie P1","Pharmacie P2","Pharmacie P3","Pharmacie P4","Pharmacie P5","Pharmacie P6",
  // Prépa
  "Prépa 1ère année (Hypokhâgne / MPSI / ECG...)","Prépa 2ème année (Khâgne / MP / PC...)",
  // BTS / BUT
  "BTS 1ère année","BTS 2ème année",
  "BUT 1ère année","BUT 2ème année","BUT 3ème année",
  // Autres
  "Licence Pro","Diplôme d\'ingénieur 1A","Diplôme d\'ingénieur 2A","Diplôme d\'ingénieur 3A",
  "École de commerce 1A","École de commerce 2A","École de commerce 3A",
  "DEUST 1ère année","DEUST 2ème année",
  "Autre"
]

const ANNEES_PAR_FILIERE = {
  medecine: ["PASS","LAS 1","LAS 2","LAS 3","DFGSM2","DFGSM3","DFASM1","DFASM2","DFASM3","Externat","Internat"],
  pharmacie: ["Pharmacie P1","Pharmacie P2","Pharmacie P3","Pharmacie P4","Pharmacie P5","Pharmacie P6"],
  prepa: ["Prépa 1ère année (MPSI/ECG/Hypokhâgne...)","Prépa 2ème année (MP/PC/Khâgne...)"],
  bts: ["BTS 1ère année","BTS 2ème année"],
  but: ["BUT 1ère année","BUT 2ème année","BUT 3ème année"],
  licence: ["L1 (Bac+1)","L2 (Bac+2)","L3 (Bac+3)"],
  master: ["M1 (Bac+4)","M2 (Bac+5)"],
  doctorat: ["Doctorat 1ère année","Doctorat 2ème année","Doctorat 3ème année","Post-doc"],
  ingenieur: ["Diplôme ingénieur 1A","Diplôme ingénieur 2A","Diplôme ingénieur 3A"],
  commerce: ["École de commerce 1A","École de commerce 2A","École de commerce 3A"],
  default: ["L1 (Bac+1)","L2 (Bac+2)","L3 (Bac+3)","M1 (Bac+4)","M2 (Bac+5)","Doctorat 1ère année","Doctorat 2ème année","Autre"]
}

function getAnneesForFiliere(filiere) {
  const f = filiere.toLowerCase()
  if (f.includes('médecine') || f.includes('medecine') || f.includes('pass') || f.includes('las') || f.includes('odontologie') || f.includes('maïeutique')) return ANNEES_PAR_FILIERE.medecine
  if (f.includes('dfgsm') || f.includes('dfasm')) return ['DFGSM2','DFGSM3','DFASM1','DFASM2','DFASM3','Externat','Internat']
  if (f.includes('pharmacie')) return ANNEES_PAR_FILIERE.pharmacie
  if (f.includes('prépa') || f.includes('prepa') || f.includes('mpsi') || f.includes('cpge') || f.includes('hypo') || f.includes('khâgne') || f.includes('ecg')) return ANNEES_PAR_FILIERE.prepa
  if (f.includes('bts')) return ANNEES_PAR_FILIERE.bts
  if (f.includes('but')) return ANNEES_PAR_FILIERE.but
  if (f.includes('ingénieur') || f.includes('ingenieur')) return [...ANNEES_PAR_FILIERE.ingenieur, ...ANNEES_PAR_FILIERE.master]
  if (f.includes('commerce') || f.includes('management') || f.includes('marketing') || f.includes('finance') || f.includes('économie') || f.includes('gestion')) return [...ANNEES_PAR_FILIERE.licence, ...ANNEES_PAR_FILIERE.master, ...ANNEES_PAR_FILIERE.commerce]
  if (f.includes('doctorat')) return ANNEES_PAR_FILIERE.doctorat
  if (f.includes('master')) return ANNEES_PAR_FILIERE.master
  return [...ANNEES_PAR_FILIERE.licence, ...ANNEES_PAR_FILIERE.master, "Autre"]
}

function DropdownSelect({ label, options, value, onChange, placeholder }) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const ref = React.useRef(null)
  const inputRef = React.useRef(null)

  const fuse = React.useMemo(() => new Fuse(options, {
    threshold: 0.4,
    distance: 100,
    keys: ['name'],
    getFn: (item) => item,
  }), [options])
  const filtered = search.length > 0
    ? fuse.search(search).map(r => r.item).slice(0, 12)
    : options.slice(0, 12)

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const select = (opt) => { onChange(opt); setSearch(''); setOpen(false) }
  const displayValue = open ? search : (value || '')

  return (
    <div ref={ref} style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: '0.78rem', color: '#7A6555', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          value={displayValue}
          placeholder={value ? value : placeholder}
          onChange={e => { setSearch(e.target.value); onChange(e.target.value); setOpen(true) }}
          onFocus={() => { setSearch(''); setOpen(true) }}
          style={{ width: '100%', padding: '11px 36px 11px 14px', background: '#fff', border: open ? '1.5px solid #B8873A' : '1px solid #E5D9CC', borderRadius: open ? '10px 10px 0 0' : 10, color: '#1A1410', fontFamily: 'var(--font-body)', fontSize: '0.92rem', outline: 'none', boxSizing: 'border-box', transition: 'border .15s' }}
        />
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, color: '#B8873A', fontSize: '0.75rem', pointerEvents: 'none', transition: 'transform .2s' }}>▼</span>
      </div>
      {open && (
        <div style={{ background: '#fff', border: '1.5px solid #B8873A', borderTop: 'none', borderRadius: '0 0 10px 10px', maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(184,135,58,.15)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 14px', color: '#7A6555', fontSize: '0.85rem', fontStyle: 'italic' }}>Aucun résultat pour "{search}"</div>
          ) : filtered.map(o => (
            <div key={o} onMouseDown={() => select(o)}
              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '0.88rem', color: o === value ? '#B8873A' : '#1A1410', fontWeight: o === value ? 700 : 400, background: '#fff', borderBottom: '1px solid #F7F3EE', transition: 'background .1s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#FBF7F2'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >{o}</div>
          ))}
        </div>
      )}
    </div>
  )
}

function OnboardingOverlay({ onDone }) {
  const [fullName, setFullName] = useState('')
  const [universite, setUniversite] = useState('')
  const [filiere, setFiliere] = useState('')
  const [annee, setAnnee] = useState('')
  const [shareContent, setShareContent] = useState(true)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light')
  }, [])

  const anneesDisponibles = filiere ? getAnneesForFiliere(filiere) : ANNEES

  async function handle() {
    setLoading(true)
    await onDone({ fullName, universite, filiere, annee, shareContent })
    setLoading(false)
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F7F3EE', padding: 24, backgroundImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, #B8873A12 0%, transparent 70%)' }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ background: '#fff', border: '1px solid #E5D9CC', borderRadius: 20, padding: 40, boxShadow: '0 20px 60px rgba(184,135,58,.08)' }}>
          <div style={{ fontSize: '2.2rem', marginBottom: 14 }}></div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.7rem', fontWeight: 700, marginBottom: 8, color: '#1A1410' }}>Ton profil étudiant</h1>
          <p style={{ color: '#7A6555', fontSize: '0.88rem', marginBottom: 32, lineHeight: 1.6 }}>
            Ces informations nous permettent de personnaliser ton expérience.
          </p>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7A6555', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Prénom et nom</div>
            <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Ton prénom et nom..."
              style={{ width: '100%', padding: '11px 14px', background: '#fff', border: '1px solid #E5D9CC', borderRadius: 10, color: '#1A1410', fontFamily: 'var(--font-body)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <DropdownSelect label="Université / École" options={UNIVERSITES} value={universite} onChange={setUniversite} placeholder="Recherche ton établissement..." />
          <DropdownSelect label="Filière" options={FILIERES} value={filiere} onChange={v => { setFiliere(v); setAnnee('') }} placeholder="Recherche ta filière..." />
          <DropdownSelect
            label={filiere ? "Année d'étude · adapté à ta filière" : "Année d'étude"}
            options={anneesDisponibles}
            value={annee}
            onChange={setAnnee}
            placeholder="Recherche ton année..."
          />
          <div onClick={() => setShareContent(v => !v)}
            style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'12px 14px', background:shareContent?'#B8873A08':'var(--bg3)', border:`1px solid ${shareContent?'#B8873A40':'var(--bd)'}`, borderRadius:10, cursor:'pointer', marginTop:8, marginBottom:4 }}>
            <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${shareContent?'#B8873A':'#ccc'}`, background:shareContent?'#B8873A':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
              {shareContent && <span style={{ color:'#fff', fontSize:'0.7rem', fontWeight:700 }}>✓</span>}
            </div>
            <span style={{ fontSize:'0.8rem', color:'#7A6555', lineHeight:1.5 }}>
              Activer le partage des contenus IA avec la communauté Flashia
            </span>
          </div>
          <button onClick={handle} disabled={loading}
            style={{ width: '100%', padding: '13px', background: '#B8873A', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', marginTop: 8 }}>
            {loading ? 'Enregistrement...' : 'Commencer →'}
          </button>
          <button onClick={() => onDone({})}
            style={{ width: '100%', marginTop: 10, padding: '10px', background: 'none', border: 'none', color: '#7A6555', cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'var(--font-body)' }}>
            Passer cette étape
          </button>
        </div>
      </div>
    </div>
  )
}


let _appInitialized = false
function NotFound({ onHome }) {
  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      background:'var(--bg)', textAlign:'center', padding:24, gap:16 }}>
      <div style={{ fontSize:'5rem' }}></div>
      <h1 style={{ fontFamily:'var(--font-display)', fontSize:'3rem', fontWeight:800, color:'var(--t1)', margin:0 }}>404</h1>
      <p style={{ color:'var(--t2)', fontSize:'1.1rem', maxWidth:400, lineHeight:1.7 }}>
        Cette page n'existe pas. Tu t'es peut-être perdu entre deux chapitres ?
      </p>
      <button onClick={onHome} style={{ marginTop:8, padding:'12px 28px', background:'var(--gold)', color:'var(--bg)',
        border:'none', borderRadius:10, fontWeight:700, fontSize:'0.95rem', cursor:'pointer', fontFamily:'var(--font-body)' }}>
        Retour à l'accueil →
      </button>
    </div>
  )
}

function CGUPage({ onBack }) {
  const s = (title, content) => (
    <div style={{marginBottom:24}}>
      <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.05rem',fontWeight:700,color:'var(--gold)',marginBottom:8,paddingBottom:6,borderBottom:'1px solid var(--bd)'}}>{title}</h2>
      <div style={{color:'var(--t2)',fontSize:'0.87rem',lineHeight:1.8}}>{content}</div>
    </div>
  )
  const li = (text) => <div style={{paddingLeft:16,color:'var(--t2)',fontSize:'0.87rem',lineHeight:1.8}}>→ {text}</div>
  const note = (text) => <div style={{background:'var(--gold)08',border:'1px solid var(--gold)30',borderLeft:'3px solid var(--gold)',borderRadius:8,padding:'10px 14px',color:'var(--t3)',fontSize:'0.82rem',fontStyle:'italic',marginTop:8}}>{text}</div>
  return (
    <div style={{minHeight:'100vh',background:'var(--bg)',padding:'40px 20px'}}>
      <div style={{maxWidth:760,margin:'0 auto'}}>
        <button onClick={onBack} style={{background:'none',border:'1px solid var(--bd)',borderRadius:8,padding:'8px 18px',color:'var(--t2)',cursor:'pointer',fontFamily:'var(--font-body)',fontSize:'0.85rem',marginBottom:32}}>← Retour</button>
        <div style={{background:'var(--bg2)',border:'1px solid var(--bd)',borderRadius:20,padding:'40px 44px'}}>
          <div style={{marginBottom:36}}>
            <div style={{fontSize:'0.72rem',color:'var(--gold)',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:8}}>Flashia — Legal</div>
            <h1 style={{fontFamily:'var(--font-display)',fontSize:'1.8rem',fontWeight:700,color:'var(--t1)',marginBottom:6}}>Conditions Générales d'Utilisation</h1>
            <p style={{color:'var(--t3)',fontSize:'0.8rem'}}>Dernière mise à jour : 15 mars 2026</p>
          </div>
          {s("1. Présentation du service", <><p>Flashia est une application web de révision intelligente accessible à www.flashia.io, éditée par Leila Benabdallah.</p><p style={{marginTop:8}}>Le service permet aux étudiants d'importer des cours au format PDF et d'obtenir automatiquement des flashcards, QCM, fiches de révision et un planning personnalisé grâce à l'IA.</p></>)}
          {s("2. Acceptation des conditions", <><p>L'utilisation du service implique l'acceptation pleine et entière des présentes CGU.</p><p style={{marginTop:8}}>Flashia se réserve le droit de modifier ces CGU à tout moment. Les utilisateurs seront informés par e-mail ou notification.</p></>)}
          {s("3. Création de compte", <><p>Pour accéder au service, vous devez créer un compte avec une adresse e-mail valide. Vous êtes responsable de la confidentialité de vos identifiants.</p><p style={{marginTop:8}}>L'accès est réservé aux personnes majeures ou aux mineurs disposant de l'autorisation parentale.</p></>)}
          {s("4. Plans et tarification", <><p><strong>Plan Gratuit</strong> — 2 cours offerts, sans carte bancaire.</p><p style={{marginTop:6}}><strong>Plan Premium</strong> — 9,99 €/mois, 20 cours/mois, génération de flashcards, QCM, fiches et plans. Sans engagement, résiliable à tout moment.</p><p style={{marginTop:6}}><strong>Packs de crédits</strong> — achat ponctuel de cours supplémentaires.</p></>)}
          {s("5. Propriété intellectuelle", <><p>Vous conservez vos droits sur les cours importés. Vos fichiers PDF sont uniquement utilisés pour générer vos contenus via l'IA — jamais partagés directement.</p><p style={{marginTop:8}}>Si vous activez le partage, les contenus générés (flashcards, fiches, plans, QCM) peuvent être réutilisés anonymement au sein de la communauté Flashia. Désactivable à tout moment dans Mon Profil.</p>{note("Cette option est activée par défaut. Elle contribue à améliorer l'expérience de toute la communauté Flashia.")}</>)}
          {s("6. Utilisation acceptable", <><p>Il est interdit de :</p><div style={{marginTop:8,display:'flex',flexDirection:'column',gap:4}}>{li("Importer des contenus illicites ou portant atteinte aux droits de tiers ;")}{li("Partager son compte avec d'autres personnes ;")}{li("Tenter de contourner les limitations du plan gratuit ;")}{li("Utiliser le service à des fins commerciales sans autorisation ;")}{li("Accéder au service par des moyens automatisés non autorisés.")}</div></>)}
          {s("7. Disponibilité & responsabilité", <><p>Flashia s'efforce d'assurer une disponibilité maximale mais ne garantit pas un accès ininterrompu. Les contenus IA sont fournis à titre indicatif — Flashia ne peut être tenu responsable des résultats académiques.</p></>)}
          {s("8. Résiliation", <p>Vous pouvez supprimer votre compte à tout moment depuis Mon Profil. Suppression définitive des données sous 30 jours.</p>)}
          {s("9. Droit applicable", <><p>Les présentes CGU sont soumises au droit français.</p>{note("Contact : contact@flashia.io")}</>)}
        </div>
      </div>
    </div>
  )
}

function ConfidentialitePage({ onBack }) {
  const s = (title, content) => (
    <div style={{marginBottom:24}}>
      <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.05rem',fontWeight:700,color:'var(--gold)',marginBottom:8,paddingBottom:6,borderBottom:'1px solid var(--bd)'}}>{title}</h2>
      <div style={{color:'var(--t2)',fontSize:'0.87rem',lineHeight:1.8}}>{content}</div>
    </div>
  )
  const li = (text) => <div style={{paddingLeft:16,color:'var(--t2)',fontSize:'0.87rem',lineHeight:1.8}}>→ {text}</div>
  const note = (text) => <div style={{background:'var(--gold)08',border:'1px solid var(--gold)30',borderLeft:'3px solid var(--gold)',borderRadius:8,padding:'10px 14px',color:'var(--t3)',fontSize:'0.82rem',fontStyle:'italic',marginTop:8}}>{text}</div>
  return (
    <div style={{minHeight:'100vh',background:'var(--bg)',padding:'40px 20px'}}>
      <div style={{maxWidth:760,margin:'0 auto'}}>
        <button onClick={onBack} style={{background:'none',border:'1px solid var(--bd)',borderRadius:8,padding:'8px 18px',color:'var(--t2)',cursor:'pointer',fontFamily:'var(--font-body)',fontSize:'0.85rem',marginBottom:32}}>← Retour</button>
        <div style={{background:'var(--bg2)',border:'1px solid var(--bd)',borderRadius:20,padding:'40px 44px'}}>
          <div style={{marginBottom:36}}>
            <div style={{fontSize:'0.72rem',color:'var(--gold)',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:8}}>Flashia — Legal</div>
            <h1 style={{fontFamily:'var(--font-display)',fontSize:'1.8rem',fontWeight:700,color:'var(--t1)',marginBottom:6}}>Politique de Confidentialité</h1>
            <p style={{color:'var(--t3)',fontSize:'0.8rem'}}>Dernière mise à jour : 15 mars 2026</p>
          </div>
          {s("1. Responsable du traitement", <><p>Leila Benabdallah, éditrice de Flashia (www.flashia.io).</p>{note("Contact : contact@flashia.io")}</>)}
          {s("2. Données collectées", <><p><strong>Inscription</strong> — e-mail, nom, université, filière, année, préférence de partage IA.</p><p style={{marginTop:6}}><strong>Traitement IA</strong> — Vos PDF sont transmis à Anthropic pour générer vos contenus. Non stockés de façon permanente, jamais partagés directement. Si le partage est activé, les contenus générés (flashcards, fiches, plans, QCM) peuvent être réutilisés anonymement dans la communauté.</p><p style={{marginTop:6}}><strong>Utilisation</strong> — Sessions de révision, progression, résultats QCM.</p><p style={{marginTop:6}}><strong>Paiement</strong> — Traité par Stripe. Aucune donnée bancaire stockée chez Flashia.</p></>)}
          {s("3. Finalités", <><div style={{display:'flex',flexDirection:'column',gap:4}}>{li("Fournir le service et générer vos contenus IA ;")}{li("Gérer votre compte et abonnement ;")}{li("Personnaliser votre expérience de révision ;")}{li("Communications relatives au service.")}</div></>)}
          {s("4. Base légale", <p>Exécution du contrat (service), consentement (partage IA, marketing), intérêts légitimes (sécurité, amélioration).</p>)}
          {s("5. Partage des données", <><p>Données jamais vendues. Partagées avec :</p><div style={{marginTop:8,display:'flex',flexDirection:'column',gap:4}}>{li("Supabase — base de données (UE) ;")}{li("Vercel — hébergement (États-Unis) ;")}{li("Stripe — paiements (États-Unis) ;")}{li("Anthropic — traitement IA (États-Unis).")}</div><p style={{marginTop:8}}>Si partage activé : les contenus générés sont partagés anonymement dans la communauté Flashia. Aucune donnée personnelle identifiable transmise.</p></>)}
          {s("6. Conservation", <><p>Données conservées pendant la durée du compte, supprimées sous 30 jours après résiliation.</p><p style={{marginTop:6}}>Données de facturation conservées 10 ans (obligation légale).</p></>)}
          {s("7. Vos droits", <><div style={{display:'flex',flexDirection:'column',gap:4}}>{li("Accès, rectification, effacement, portabilité ;")}{li("Retrait du consentement au partage IA (via Mon Profil) ;")}{li("Opposition au traitement — à noter : le traitement IA étant inhérent au service, l'opposition totale entraîne la résiliation du compte.")}</div>{note("Pour exercer vos droits : contact@flashia.io — Réclamation possible à la CNIL (www.cnil.fr)")}</>)}
          {s("8. Cookies", <p>Uniquement des cookies essentiels (authentification, thème). Aucun cookie publicitaire.</p>)}
          {s("9. Sécurité", <p>Chiffrement HTTPS, authentification sécurisée Supabase, accès restreint aux données.</p>)}
        </div>
      </div>
    </div>
  )
}

function AppInner() {
  const [session, setSession] = useState(undefined) // undefined = loading
  const [profile, setProfile] = useState(null)
  const [needsOnboarding, setNeedsOnboarding] = useState(null) // null=loading, false=non, true=oui
  const onboardingDone = React.useRef(false)
  const profileLoading = React.useRef(false)
  // Utilisateur déjà en place : Supabase réémet SIGNED_IN à chaque retour sur l'onglet, et on
  // ne veut recharger le profil que s'il s'agit réellement de quelqu'un d'autre.
  const sessionUid = React.useRef(null)
  const navigate = useNavigate() // 'landing' | 'auth' | 'app' | 'pricing' | 'cgu' | 'confidentialite'
  const [showResetForm, setShowResetForm] = useState(false)
  const [pendingSection, setPendingSection] = useState(null)
  const [theme, setTheme] = useState('light')

  // Cursor glow global
  useEffect(() => {
    const cursor = document.createElement('div')
    cursor.id = 'sf-cursor-glow'
    cursor.style.cssText = `
      position:fixed;width:400px;height:400px;border-radius:50%;
      pointer-events:none;z-index:9999;
      background:radial-gradient(circle,rgba(201,169,110,.08) 0%,transparent 70%);
      transform:translate(-50%,-50%);transition:opacity .3s;
      top:-999px;left:-999px;
    `
    document.body.appendChild(cursor)
    const move = e => { cursor.style.left = e.clientX+'px'; cursor.style.top = e.clientY+'px' }
    const enter = () => cursor.style.opacity = '1'
    const leave = () => cursor.style.opacity = '0'
    window.addEventListener('mousemove', move, { passive:true })
    document.addEventListener('mouseenter', enter)
    document.addEventListener('mouseleave', leave)
    return () => {
      window.removeEventListener('mousemove', move)
      document.removeEventListener('mouseenter', enter)
      document.removeEventListener('mouseleave', leave)
      cursor.remove()
    }
  }, [])

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light')
    localStorage.setItem('sf-theme', 'light')
  }, [theme])

  // Global styles injected via <style> tag in return below

  // Auth state listener
  useEffect(() => {
    // Détecter recovery
    const hash = window.location.hash
    const urlPath = window.location.pathname
    if (urlPath === '/cgu') { navigate('/cgu'); return }
    if (urlPath === '/confidentialite') { navigate('/confidentialite'); return }
    if (urlPath === '/inscription-ecurie') { navigate('/inscription-ecurie'); return }
    if (hash.includes('error=') && hash.includes('otp_expired')) {
      navigate('/auth'); setSession(null); setNeedsOnboarding(false)
    } else if (urlPath === '/reset-password' || hash.includes('type=recovery')) {
      window._isRecovery = true
      setShowResetForm(true)
      navigate('/auth')
      setNeedsOnboarding(false)
    }

    // Chargement initial unique
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (window._isRecovery) return
      if (session) { sessionUid.current = session.user.id; loadProfile(session.user.id); if (urlPath !== '/inscription-ecurie' && !urlPath.startsWith('/app') && !urlPath.startsWith('/patron') && !urlPath.startsWith('/tuteur') && !urlPath.startsWith('/demo-prepa') && !urlPath.startsWith('/demo-etudiant')) navigate('/app') }
      else { setSession(null) }
    })

    // Écouter seulement déconnexion et recovery
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      if (_e === 'SIGNED_OUT') {
        setSession(null); setProfile(null); navigate('/')
        profileLoading.current = null
        sessionUid.current = null
        window._isRecovery = false
        return
      }
      if (_e === 'PASSWORD_RECOVERY') {
        setSession(s); setShowResetForm(true); navigate('/auth'); return
      }
      if (_e === 'SIGNED_IN' && s && !window._isRecovery) {
        // Revenir sur l'onglet réémet SIGNED_IN avec le MÊME utilisateur. Recharger son profil
        // à ce moment-là ne fait qu'un aller-retour réseau de plus, et remplace l'objet profil
        // par un nouveau — donc un rendu complet de l'app pour rien.
        const memeUtilisateur = sessionUid.current === s.user.id
        sessionUid.current = s.user.id
        setSession(s)
        if (!memeUtilisateur) {
          profileLoading.current = null // force reload même si déjà tenté
          loadProfile(s.user.id)
        }
        // Only navigate to /app on first sign-in, not on token refresh
        const currentPath = window.location.pathname
        if (!currentPath.startsWith('/app') && !currentPath.startsWith('/patron') && !currentPath.startsWith('/tuteur') && !currentPath.startsWith('/demo-prepa') && !currentPath.startsWith('/demo-etudiant')) {
          navigate('/app')
        }
        return
      }
      // Ignorer TOKEN_REFRESHED et INITIAL_SESSION
    })
    return () => subscription.unsubscribe()
  }, [])

  const loadProfile = async (uid, retry = 0) => {
    if (!uid) return
    if (profileLoading.current === uid && retry === 0) return
    profileLoading.current = uid
    const { data } = await supabase.from('profiles').select('*').eq('id', uid).single()
    if (!data && retry < 3) {
      await new Promise(r => setTimeout(r, 300))
      return loadProfile(uid, retry + 1)
    }
    if (data?.role === 'admin' || data?.role === 'tuteur' || data?.role_ecurie === 'patron' || data?.role_ecurie === 'patron_pending' || data?.filiere || data?.annee || data?.onboarding_done) { setNeedsOnboarding(false); onboardingDone.current = true }
    // Acceptation d'une invitation tuteur en attente — via RPC SECURITY DEFINER :
    // elle pose le rôle 'tuteur' (bloqué côté client par la protection des profils),
    // crée les assignations et marque l'invitation utilisée, atomiquement.
    // (Avant : code client référençant une variable `userId` inexistante → échec/crash.)
    if (data?.role !== 'tuteur' && data?.role_ecurie !== 'patron' && data?.email) {
      try {
        const { data: accepted } = await supabase.rpc('accept_tuteur_invitation')
        if (accepted === true) data.role = 'tuteur'
      } catch (e) { logError('accept_tuteur_invitation:', e) }
    }
    if (data?.role !== 'tuteur' && !onboardingDone.current) { setNeedsOnboarding(true) }
    if (data) setProfile(data)
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  // Loading screen — ne pas bloquer les pages publiques
  const publicRoutes = ['/', '/auth', '/demo', '/demo-prepa', '/demo-etudiant', '/pricing', '/cgu', '/confidentialite', '/inscription-ecurie']
  const isPublicRoute = publicRoutes.some(r => window.location.pathname === r || window.location.pathname.startsWith(r + '/'))
  if (session === undefined && !isPublicRoute) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16 }}>
      <div style={{ width:40, height:40, border:'3px solid var(--bd)', borderTopColor:'var(--gold)', borderRadius:'50%' }} className="spinner" />
      <p style={{ color:'var(--t3)', fontSize:'0.85rem' }}>Chargement…</p>
    </div>
  )

  // ── À APPELER, PAS À MONTER (15/09) ──────────────────────────────────────────
  // Cette fonction est définie DANS App, donc React reçoit une nouvelle identité de
  // composant à chaque rendu d'App. Utilisée en `<AppContent />`, elle faisait démonter puis
  // remonter tout le Dashboard au moindre re-rendu : chapitres refermés, position de
  // défilement perdue, écran qui « saute ». Appelée en `{AppContent()}`, son JSX s'insère
  // directement dans l'arbre d'App — plus de frontière de composant, donc plus de remontage.
  const AppContent = () => {
    if (!session) return null
    if (needsOnboarding === null) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}><div style={{width:36,height:36,border:'3px solid var(--bd)',borderTopColor:'var(--gold)',borderRadius:'50%'}} className="spinner"/></div>
    if (needsOnboarding === true) return (
      <OnboardingOverlay onDone={async ({ fullName, universite, filiere, annee, shareContent }) => {
        const { error: upErr } = await supabase.from('profiles').upsert({
          id: session.user.id,
          full_name: fullName?.trim() || null,
          universite, filiere, annee,
          share_content: shareContent !== false,
          onboarding_done: true
        }, { onConflict: 'id' })
        if (upErr) {
          logError('onboarding upsert:', upErr)
        } else {
          setProfile(p => ({ ...p, full_name: fullName?.trim() || null, universite, filiere, annee, share_content: shareContent !== false, onboarding_done: true }))
          onboardingDone.current = true
          setNeedsOnboarding(false)
        }
      }} />
    )
    if (profile?.role === 'tuteur') return <TuteurDashboard />
    if (profile?.role_ecurie === 'patron') return <PatronDashboard />
    if (profile?.role_ecurie === 'patron_pending') return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16, padding:24 }}>
        <div style={{ fontSize:48 }}>⏳</div>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, color:'var(--t1)' }}>Votre préparatoire privée est en cours de validation</h2>
        <p style={{ color:'var(--t3)', fontSize:'0.88rem', textAlign:'center', maxWidth:400 }}>Votre demande a été reçue. Vous recevrez un email dès que votre espace sera active par l'équipe Flashia.</p>
        <button onClick={signOut} style={{ marginTop:12, padding:'10px 24px', borderRadius:10, border:'1px solid var(--bd)', background:'transparent', color:'var(--t2)', cursor:'pointer', fontFamily:'var(--font-body)', fontWeight:600 }}>Se déconnecter</button>
      </div>
    )
    return <Dashboard />
  }

  return (
    <>
    <style>{globalStyle}</style>
    <AuthCtx.Provider value={{ session, profile, signOut, reloadProfile: () => loadProfile(session?.user?.id), theme, setTheme, setPage: navigate, pendingSection, setPendingSection }}>
      <ErrorBoundary>
        <Suspense fallback={<div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}><div style={{ width:28, height:28, border:'3px solid #e5d9cc', borderTopColor:'#c9a96e', borderRadius:'50%', animation:'spin .8s linear infinite' }} /></div>}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<LandingPage onLogin={() => navigate('/auth')} onPricing={() => navigate('/pricing')} onDemo={() => navigate('/demo')} onEcurie={() => navigate('/inscription-ecurie')} />} />
            <Route path="/auth" element={<AuthPage onBack={() => navigate('/')} />} />
            <Route path="/demo" element={<DemoPage onSignup={() => navigate('/auth')} onBack={() => navigate('/')} />} />
            <Route path="/demo-prepa/*" element={<PatronDemoPublic />} />
            <Route path="/demo-etudiant/*" element={<StudentDemoPublic />} />
            <Route path="/pricing" element={<PricingPage onClose={() => navigate(session ? '/app' : '/')} />} />
            <Route path="/cgu" element={<CGUPage onBack={() => navigate('/')} />} />
            <Route path="/confidentialite" element={<ConfidentialitePage onBack={() => navigate('/')} />} />
            <Route path="/inscription-ecurie" element={<InscriptionEcurie onBack={() => navigate('/')} />} />
            <Route path="/app/*" element={session ? AppContent() : <Navigate to="/" replace />} />
            <Route path="/tuteur/*" element={session ? AppContent() : <Navigate to="/" replace />} />
            <Route path="/patron/*" element={session ? AppContent() : <Navigate to="/" replace />} />
            <Route path="/parent/:token" element={<ParentView />} />
            <Route path="*" element={<NotFound onHome={() => navigate('/')} />} />
          </Routes>
          </ErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    </AuthCtx.Provider>
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  )
}
